import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  List,
  ListItemButton,
  MenuItem,
  Paper,
  Stack,
  TextField as MuiTextField,
  Tooltip,
  Typography,
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import { useNavigate } from "react-router-dom";
import { useDataProvider, useNotify } from "react-admin";
import { formatDate } from "../../utils/common";
import type { TicketListItem } from "../../models/Ticket";
import type { AppDataProvider } from "../../types/dataProvider";
import {
  getReplySenderIdentityLabel,
  getTicketRelatedDetailTarget,
  getTicketTypeColor,
  getTicketTypeLabel,
  isAiReply,
} from "./utils";

type LoadState = "loading" | "ready" | "error";

const STATUS_ALL = "all";
const STATUS_WITH_CONTENT = "with_content";
const STATUS_EMPTY = "empty";

interface SenderUserBrief {
  id: string;
  name: string;
  role?: number | null;
}

/**
 * 从用户记录中解析展示用 id / 昵称 / 身份。
 * @param item 用户或司机记录
 * @returns 简要发送人信息，无法识别时返回 null
 */
const parseSenderUserBrief = (item: unknown): SenderUserBrief | null => {
  if (!item || typeof item !== "object") {
    return null;
  }
  const record = item as Record<string, unknown>;
  const idValue = record.id ?? record._id;
  const id =
    typeof idValue === "string" || typeof idValue === "number"
      ? String(idValue)
      : "";
  if (!id) {
    return null;
  }
  const nameCandidates = [
    record.name,
    record.nickname,
    record.username,
    record.real_name,
    record.realname,
    record.fullName,
  ];
  let name = "";
  for (const candidate of nameCandidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      name = candidate.trim();
      break;
    }
  }
  const roleRaw = record.role;
  const role =
    typeof roleRaw === "number" && Number.isFinite(roleRaw)
      ? roleRaw
      : typeof roleRaw === "string" && roleRaw.trim() !== ""
        ? Number(roleRaw)
        : null;
  return {
    id,
    name,
    role: role !== null && Number.isFinite(role) ? role : null,
  };
};

const CommunicationListPage: React.FC = () => {
  const dataProvider = useDataProvider() as AppDataProvider;
  const notify = useNotify();
  const navigate = useNavigate();

  const [tickets, setTickets] = React.useState<TicketListItem[]>([]);
  const [senderUsersById, setSenderUsersById] = React.useState<
    Record<string, SenderUserBrief>
  >({});
  const [loadState, setLoadState] = React.useState<LoadState>("loading");
  const [errorMessage, setErrorMessage] = React.useState<string>("");
  const [refreshing, setRefreshing] = React.useState(false);

  const [typeFilter, setTypeFilter] = React.useState<string>("all");
  const [contentFilter, setContentFilter] = React.useState<string>(STATUS_WITH_CONTENT);
  const [searchKeyword, setSearchKeyword] = React.useState<string>("");

  const fetchTickets = React.useCallback(
    async (mode: "initial" | "refresh") => {
      if (mode === "initial") {
        setLoadState("loading");
      } else {
        setRefreshing(true);
      }
      setErrorMessage("");
      try {
        const response = await dataProvider.getTickets();
        const sorted = [...response.data].sort((a, b) => {
          if (a.updatedAt && b.updatedAt) {
            return b.updatedAt.localeCompare(a.updatedAt);
          }
          return 0;
        });
        setTickets(sorted);
        setLoadState("ready");
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "沟通记录加载失败";
        setErrorMessage(message);
        setLoadState((previous) => (previous === "ready" ? previous : "error"));
        notify(`沟通记录加载失败：${message}`, { type: "error" });
      } finally {
        setRefreshing(false);
      }
    },
    [dataProvider, notify]
  );

  React.useEffect(() => {
    void fetchTickets("initial");
  }, [fetchTickets]);

  /**
   * 按最新回复的发送人 ID 补全昵称与身份（货主/司机）。
   */
  React.useEffect(() => {
    const senderIds = Array.from(
      new Set(
        tickets
          .map((ticket) => ticket.latestReply?.senderId)
          .filter((id): id is string => Boolean(id))
      )
    );
    if (senderIds.length === 0) {
      setSenderUsersById({});
      return;
    }

    let cancelled = false;
    const fetchSenderUsers = async () => {
      try {
        const [usersResponse, driversResponse] = await Promise.all([
          dataProvider.getMany("users", { ids: senderIds }),
          dataProvider
            .getMany("drivers", { ids: senderIds })
            .catch(() => ({ data: [] as unknown[] })),
        ]);
        if (cancelled) {
          return;
        }
        const next: Record<string, SenderUserBrief> = {};
        const mergeRecord = (item: unknown) => {
          const brief = parseSenderUserBrief(item);
          if (!brief) {
            return;
          }
          const existing = next[brief.id];
          next[brief.id] = {
            id: brief.id,
            name: brief.name || existing?.name || "",
            role: brief.role ?? existing?.role ?? null,
          };
        };
        (Array.isArray(usersResponse.data) ? usersResponse.data : []).forEach(mergeRecord);
        (Array.isArray(driversResponse.data) ? driversResponse.data : []).forEach(mergeRecord);
        setSenderUsersById(next);
      } catch {
        if (!cancelled) {
          setSenderUsersById({});
        }
      }
    };

    void fetchSenderUsers();
    return () => {
      cancelled = true;
    };
  }, [dataProvider, tickets]);

  const handleRefresh = () => {
    void fetchTickets("refresh");
  };

  const handleOpen = (ticketId: string) => {
    if (!ticketId) {
      return;
    }
    navigate(`/communication/${ticketId}`);
  };

  /**
   * 跳转到工单关联的货源或订单详情。
   * @param event 点击事件，需阻止冒泡以免进入沟通详情
   * @param ticket 列表工单
   */
  const handleOpenRelatedDetail = (
    event: React.MouseEvent,
    ticket: TicketListItem
  ) => {
    event.preventDefault();
    event.stopPropagation();
    const target = getTicketRelatedDetailTarget(ticket.type, ticket.subject);
    if (!target) {
      notify("缺少关联对象 ID，无法打开详情", { type: "warning" });
      return;
    }
    navigate(target.path);
  };

  const typeOptions = React.useMemo(() => {
    const seen = new Set<string>();
    const options: Array<{ value: string; label: string }> = [
      { value: "all", label: "全部类型" },
    ];
    tickets.forEach((ticket) => {
      const raw = ticket.type ?? "";
      const key = raw === "" ? "客服" : String(raw);
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      options.push({ value: key, label: getTicketTypeLabel(key) });
    });
    return options;
  }, [tickets]);

  const filteredTickets = React.useMemo(() => {
    const keyword = searchKeyword.trim().toLowerCase();
    return tickets.filter((ticket) => {
      if (typeFilter !== "all") {
        const raw = ticket.type ?? "";
        const key = raw === "" ? "客服" : String(raw);
        if (key !== typeFilter) {
          return false;
        }
      }
      if (contentFilter === STATUS_WITH_CONTENT) {
        if (!ticket.latestReply?.content?.trim()) {
          return false;
        }
      } else if (contentFilter === STATUS_EMPTY) {
        if (ticket.latestReply?.content?.trim()) {
          return false;
        }
      }
      if (keyword) {
        const senderId = ticket.latestReply?.senderId ?? "";
        const senderUser = senderId ? senderUsersById[senderId] : undefined;
        const senderName =
          senderUser?.name || ticket.latestReply?.senderName || "";
        const identityLabel = ticket.latestReply
          ? getReplySenderIdentityLabel(ticket.latestReply, senderUser?.role)
          : "";
        const haystack = `${ticket.title ?? ""} ${
          ticket.latestReply?.content ?? ""
        } ${senderName} ${identityLabel}`.toLowerCase();
        if (!haystack.includes(keyword)) {
          return false;
        }
      }
      return true;
    });
  }, [tickets, typeFilter, contentFilter, searchKeyword, senderUsersById]);

  const summary = React.useMemo(() => {
    const total = tickets.length;
    const ai = tickets.filter((item) => item.aiTagged).length;
    const empty = tickets.filter(
      (item) => !item.latestReply?.content?.trim()
    ).length;
    return { total, ai, empty };
  }, [tickets]);

  const resetFilters = () => {
    setTypeFilter("all");
    setContentFilter(STATUS_WITH_CONTENT);
    setSearchKeyword("");
  };

  const renderList = () => {
    if (loadState === "loading") {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight={240}
          gap={2}
        >
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary">
            正在加载沟通记录...
          </Typography>
        </Box>
      );
    }

    if (loadState === "error" && tickets.length === 0) {
      return (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              重试
            </Button>
          }
        >
          {errorMessage || "沟通记录加载失败"}
        </Alert>
      );
    }

    if (tickets.length === 0) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight={240}
          gap={1}
        >
          <Typography variant="body1" color="text.secondary">
            暂无沟通记录
          </Typography>
        </Box>
      );
    }

    if (filteredTickets.length === 0) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight={240}
          gap={1}
        >
          <Typography variant="body1" color="text.secondary">
            没有匹配的沟通记录
          </Typography>
          <Button size="small" onClick={resetFilters}>
            清空筛选
          </Button>
        </Box>
      );
    }

    return (
      <List disablePadding>
        {filteredTickets.map((ticket) => {
          const latestReply = ticket.latestReply;
          const summaryText = latestReply?.content?.trim()
            ? latestReply.content
            : "暂无沟通内容";
          const senderUser = latestReply?.senderId
            ? senderUsersById[latestReply.senderId]
            : undefined;
          const senderName =
            senderUser?.name || latestReply?.senderName || "";
          const identityLabel = latestReply
            ? getReplySenderIdentityLabel(latestReply, senderUser?.role)
            : "";
          const isAiLatestReply = Boolean(latestReply && isAiReply(latestReply));
          const isOwnerSender = identityLabel === "货主";
          const relatedDetail = getTicketRelatedDetailTarget(
            ticket.type,
            ticket.subject
          );
          return (
            <React.Fragment key={ticket.id}>
              <ListItemButton
                component="div"
                alignItems="flex-start"
                onClick={() => handleOpen(ticket.id)}
                disabled={!ticket.id}
                sx={{
                  px: 2,
                  py: 1.5,
                  "&:hover": {
                    backgroundColor: (theme) => theme.palette.action.hover,
                  },
                }}
              >
                <Stack spacing={0.5} sx={{ width: "100%" }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                  >
                    <Chip
                      size="small"
                      color={getTicketTypeColor(ticket.type)}
                      label={getTicketTypeLabel(ticket.type)}
                      sx={{ height: 22 }}
                    />
                    {ticket.aiTagged ? (
                      <Chip
                        size="small"
                        color="info"
                        variant="outlined"
                        icon={<SmartToyIcon sx={{ fontSize: 14 }} />}
                        label="AI"
                        sx={{ height: 22 }}
                      />
                    ) : null}
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        flex: 1,
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {ticket.title || "沟通工单"}
                    </Typography>
                    {relatedDetail ? (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                        onClick={(event) => handleOpenRelatedDetail(event, ticket)}
                        sx={{ flexShrink: 0, height: 28 }}
                      >
                        详情
                      </Button>
                    ) : null}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ flexShrink: 0 }}
                    >
                      {ticket.id}
                    </Typography>
                  </Stack>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      wordBreak: "break-all",
                    }}
                  >
                    {senderName || identityLabel ? (
                      <Box
                        component="span"
                        sx={{
                          // AI 回复不高亮；货主用黄色，其他发送人保持主题色高亮
                          color: isAiLatestReply
                            ? "inherit"
                            : isOwnerSender
                              ? "warning.main"
                              : "primary.main",
                          fontWeight: isAiLatestReply ? 400 : 700,
                          mr: 0.5,
                        }}
                      >
                        {senderName || (isAiLatestReply ? "AI 助手" : "未知用户")}
                        {identityLabel ? `（${identityLabel}）` : ""}：
                      </Box>
                    ) : null}
                    {summaryText}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{ color: "text.secondary" }}
                  >
                    <Typography variant="caption">
                      创建 {ticket.createdAt ? formatDate(ticket.createdAt) : "-"}
                    </Typography>
                    <Typography variant="caption">
                      更新 {ticket.updatedAt ? formatDate(ticket.updatedAt) : "-"}
                    </Typography>
                  </Stack>
                </Stack>
              </ListItemButton>
              <Divider component="li" />
            </React.Fragment>
          );
        })}
      </List>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
      >
        <Stack direction="row" spacing={1.5} alignItems="baseline">
          <Typography variant="h6" fontWeight={700}>
            沟通管理
          </Typography>
          <Typography variant="body2" color="text.secondary">
            共 {summary.total} 条工单 · {summary.ai} 条含 AI 回复 · {summary.empty} 条暂无内容
          </Typography>
        </Stack>
        <Tooltip title="刷新">
          <span>
            <IconButton size="small" onClick={handleRefresh} disabled={refreshing}>
              {refreshing ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {errorMessage && tickets.length > 0 ? (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          数据刷新失败：{errorMessage}
        </Alert>
      ) : null}

      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} lg={8.5} xl={9}>
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              minHeight: 480,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                px: 2,
                py: 1.25,
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Typography variant="subtitle2">
                工单列表（{filteredTickets.length}）
              </Typography>
            </Stack>
            {renderList()}
          </Paper>
        </Grid>

        <Grid item xs={12} lg={3.5} xl={3}>
          <Box
            sx={{
              position: { xs: "static", lg: "sticky" },
              top: { lg: 16 },
            }}
          >
            <Paper variant="outlined" sx={{ borderRadius: 2 }}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  px: 2,
                  py: 1.25,
                  borderBottom: 1,
                  borderColor: "divider",
                }}
              >
                <FilterAltIcon fontSize="small" color="action" />
                <Typography variant="subtitle2">筛选条件</Typography>
              </Stack>

              <Stack spacing={1.5} sx={{ p: 2 }}>
                <MuiTextField
                  select
                  size="small"
                  label="工单类型"
                  value={typeFilter}
                  onChange={(event) => setTypeFilter(event.target.value)}
                >
                  {typeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </MuiTextField>

                <MuiTextField
                  select
                  size="small"
                  label="最新回复"
                  value={contentFilter}
                  onChange={(event) => setContentFilter(event.target.value)}
                >
                  <MenuItem value={STATUS_ALL}>全部</MenuItem>
                  <MenuItem value={STATUS_WITH_CONTENT}>有内容</MenuItem>
                  <MenuItem value={STATUS_EMPTY}>暂无内容</MenuItem>
                </MuiTextField>

                <MuiTextField
                  size="small"
                  label="关键字"
                  placeholder="标题或最新回复"
                  value={searchKeyword}
                  onChange={(event) => setSearchKeyword(event.target.value)}
                  InputProps={{
                    startAdornment: (
                      <SearchIcon
                        fontSize="small"
                        sx={{ mr: 1, color: "text.disabled" }}
                      />
                    ),
                  }}
                />

                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button size="small" variant="text" onClick={resetFilters}>
                    重置
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CommunicationListPage;